/**
 * Frontend time utility functions for display formatting only
 * All time calculations are done on the backend
 */

export interface TimeFormat {
  hours: number;
  minutes: number;
}

/**
 * Format time as "Xh Ym" string for display
 * @param hours - Whole hours
 * @param minutes - Minutes
 * @returns Formatted string like "2h 30m"
 */
export function formatTimeString(hours: number, minutes: number): string {
  if (hours === 0 && minutes === 0) {
    return '0m';
  }

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  return parts.join(' ');
}

/**
 * Format decimal hours as "Xh Ym" string for display
 * @param decimalHours - Hours in decimal format
 * @returns Formatted string like "2h 30m"
 */
export function formatDecimalHours(decimalHours: number): string {
  const { hours, minutes } = decimalToHoursMinutes(decimalHours);
  return formatTimeString(hours, minutes);
}

/**
 * Convert decimal hours to hours and minutes for display
 * @param decimalHours - Hours in decimal format (e.g., 2.5 = 2 hours 30 minutes)
 * @returns Object with hours and minutes
 */
export function decimalToHoursMinutes(decimalHours: number): TimeFormat {
  if (isNaN(decimalHours) || decimalHours < 0) {
    return { hours: 0, minutes: 0 };
  }

  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours % 1) * 60);

  // Handle edge case where rounding gives us 60 minutes
  if (minutes === 60) {
    return { hours: hours + 1, minutes: 0 };
  }

  return { hours, minutes };
}

/**
 * Format time for display (HH:MM -> "H:MM AM/PM")
 */
export function formatTimeForDisplay(timeString: string): string {
  if (!timeString) return '';

  // Handle datetime strings (e.g., "1970-01-01T08:30:00.000Z")
  if (timeString.includes('T') && timeString.includes('Z')) {
    const date = new Date(timeString);
    if (!isNaN(date.getTime())) {
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    }
  }

  // Handle both HH:MM and HH:MM:SS formats
  const timeParts = timeString.split(':');
  const hours = parseInt(timeParts[0], 10);
  const minutes = parseInt(timeParts[1], 10);

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Format hour number for display (e.g., 14 -> "2:00 PM")
 */
export function formatHour(hour: number): string {
  if (hour === 0) return '12:00 AM';
  if (hour < 12) return `${hour}:00 AM`;
  if (hour === 12) return '12:00 PM';
  return `${hour - 12}:00 PM`;
}

/**
 * Calculate end time given start time and duration (for display only)
 * @param startTime - Start time in "HH:MM" format
 * @param durationHours - Duration in decimal hours
 * @returns End time in "HH:MM" format
 */
export function calculateEndTime(startTime: string, durationHours: number): string {
  if (!startTime || isNaN(durationHours)) {
    return 'N/A';
  }

  const [startHour, startMinute] = startTime.split(':').map(Number);
  if (isNaN(startHour) || isNaN(startMinute)) {
    return 'N/A';
  }

  const startDate = new Date();
  startDate.setHours(startHour, startMinute, 0, 0);

  const durationMs = durationHours * 60 * 60 * 1000;
  const endDate = new Date(startDate.getTime() + durationMs);

  const endHour = endDate.getHours().toString().padStart(2, '0');
  const endMinute = endDate.getMinutes().toString().padStart(2, '0');

  return `${endHour}:${endMinute}`;
}

/**
 * Format date string for display
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format date and time for display
 * @param dateString - ISO datetime string
 * @returns Formatted datetime string
 */
export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Calendar date in the user's local timezone (for HTML date inputs and API params).
 * Prefer this over `toISOString().split('T')[0]`, which is UTC and can be the wrong local day.
 */
export function to_local_yyyy_mm_dd(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Get current date in YYYY-MM-DD format
 * @returns Current date string
 */
export function getCurrentDateYMD(): string {
  return to_local_yyyy_mm_dd();
}

/**
 * Add days to a date string
 * @param dateString - YYYY-MM-DD date string
 * @param days - Number of days to add
 * @returns New date string in YYYY-MM-DD format
 */
export function addDays(dateString: string, days: number): string {
  const date = new Date(dateString + 'T00:00:00Z');
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().split('T')[0];
}

/**
 * Convert time string to decimal hours
 * @param timeString - Time in HH:MM or HH:MM:SS format
 * @returns Decimal hours
 */
export function timeStringToDecimalHours(timeString: string): number {
  if (!timeString) return 0;

  // Handle datetime strings like "1970-01-01T12:00:00.000Z"
  if (timeString.includes('T')) {
    const timePart = timeString.split('T')[1].split('.')[0]; // Extract "12:00:00" from "1970-01-01T12:00:00.000Z"
    const parts = timePart.split(':');
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const seconds = parseInt(parts[2], 10) || 0;
    return hours + minutes / 60 + seconds / 3600;
  }

  // Handle simple time strings like "12:00:00"
  const parts = timeString.split(':');
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  const seconds = parseInt(parts[2], 10) || 0;

  return hours + minutes / 60 + seconds / 3600;
}

/**
 * Convert decimal hours to time string
 * @param decimalHours - Hours in decimal format
 * @returns Time string in HH:MM:SS format
 */
export function decimalHoursToTimeString(decimalHours: number): string {
  const hours = Math.floor(decimalHours);
  const minutes = Math.floor((decimalHours % 1) * 60);
  const seconds = Math.floor((((decimalHours % 1) * 60) % 1) * 60);

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Calculate duration between two times
 * @param startTime - Start time in "HH:MM" format
 * @param endTime - End time in "HH:MM" format
 * @returns Duration in decimal hours
 */
function calculateDuration(startTime: string, endTime: string): number {
  if (!startTime || !endTime) {
    return 0;
  }

  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  if (isNaN(startHour) || isNaN(startMinute) || isNaN(endHour) || isNaN(endMinute)) {
    return 0;
  }

  const startDate = new Date();
  startDate.setHours(startHour, startMinute, 0, 0);

  const endDate = new Date();
  endDate.setHours(endHour, endMinute, 0, 0);

  // Handle case where end time is next day
  if (endDate < startDate) {
    endDate.setDate(endDate.getDate() + 1);
  }

  const durationMs = endDate.getTime() - startDate.getTime();
  return durationMs / (60 * 60 * 1000);
}

/**
 * Round time to nearest minute to avoid floating point precision errors
 */
function roundToMinute(hours: number): number {
  // Round to nearest minute, but ensure we don't create unnecessary gaps
  return Math.round(hours * 60) / 60; // Round to nearest minute (1/60 hour)
}

/**
 * Calculate effective working hours excluding break time
 * Matches backend implementation for consistency - handles edge cases like breaks outside work hours
 * @param startTime - Start time in HH:MM format
 * @param endTime - End time in HH:MM format
 * @param breakStartTime - Break start time in HH:MM format
 * @param breakEndTime - Break end time in HH:MM format
 * @returns Effective working hours in decimal format
 */
export function calculateEffectiveHours(
  startTime: string,
  endTime: string,
  breakStartTime: string,
  breakEndTime: string
): number {
  const totalMinutes = calculateDuration(startTime, endTime) * 60;
  let breakMinutes = 0;

  if (breakStartTime && breakEndTime) {
    const breakStart = timeStringToDecimalHours(breakStartTime);
    const breakEnd = timeStringToDecimalHours(breakEndTime);
    const workStart = timeStringToDecimalHours(startTime);
    const workEnd = timeStringToDecimalHours(endTime);

    if (breakStart >= workStart && breakEnd <= workEnd) {
      // Break is fully within work hours
      breakMinutes = calculateDuration(breakStartTime, breakEndTime) * 60;
    } else if (breakStart < workStart && breakEnd > workStart) {
      // Break starts before work and overlaps into work hours
      breakMinutes = calculateDuration(startTime, breakEndTime) * 60;
    } else if (breakStart < workEnd && breakEnd > workEnd) {
      // Break extends beyond work end time
      breakMinutes = calculateDuration(breakStartTime, endTime) * 60;
    }
  }

  const effectiveMinutes = Math.max(0, totalMinutes - breakMinutes);
  return roundToMinute(effectiveMinutes / 60);
}

/**
 * Validate working hours configuration
 * @param startTime - Start time in HH:MM format
 * @param endTime - End time in HH:MM format
 * @param breakStartTime - Break start time in HH:MM format
 * @param breakEndTime - Break end time in HH:MM format
 * @returns Validation result with errors
 */
export function validateWorkingHours(
  startTime: string,
  endTime: string,
  breakStartTime: string,
  breakEndTime: string
): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Parse times
  const start = timeStringToDecimalHours(startTime);
  const end = timeStringToDecimalHours(endTime);
  const breakStart = timeStringToDecimalHours(breakStartTime);
  const breakEnd = timeStringToDecimalHours(breakEndTime);

  // Validate start and end times
  if (start >= end) {
    errors.push('Start time must be before end time');
  }

  // Validate break times
  if (breakStart >= breakEnd) {
    errors.push('Break start time must be before break end time');
  }

  // Validate break is within working hours
  if (breakStart < start || breakEnd > end) {
    errors.push('Break time must be within working hours');
  }

  // Validate break doesn't overlap with start/end
  if (breakStart <= start || breakEnd >= end) {
    errors.push('Break time cannot overlap with start or end of working hours');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Format time for API (HH:MM -> HH:MM:SS)
 * @param timeString - Time in HH:MM format
 * @returns Time in HH:MM:SS format
 */
export function formatTimeForApi(timeString: string): string {
  if (!timeString) return '00:00:00';

  // If already in HH:MM:SS format, return as is
  if (timeString.split(':').length === 3) {
    return timeString;
  }

  // Convert HH:MM to HH:MM:SS
  return `${timeString}:00`;
}

/**
 * Format time from API (HH:MM:SS -> HH:MM)
 * @param timeString - Time in HH:MM:SS format
 * @returns Time in HH:MM format
 */
export function formatTimeFromApi(timeString: string): string {
  if (!timeString) return '00:00';

  // If already in HH:MM format, return as is
  if (timeString.split(':').length === 2) {
    return timeString;
  }

  // Convert HH:MM:SS to HH:MM
  const parts = timeString.split(':');
  return `${parts[0]}:${parts[1]}`;
}

/**
 * Extract time string from working hours object or string
 * @param timeValue - Time value (string or object with time property)
 * @returns Time string in HH:MM format
 */
export function extractTimeString(timeValue: any): string {
  if (!timeValue) return '00:00';

  // If it's already a string, return it
  if (typeof timeValue === 'string') {
    return timeValue;
  }

  // If it's an object with a time property, extract it
  if (typeof timeValue === 'object' && timeValue.time) {
    return timeValue.time;
  }

  // If it's an object with start_time or end_time, extract it
  if (typeof timeValue === 'object' && (timeValue.start_time || timeValue.end_time)) {
    return timeValue.start_time || timeValue.end_time || '00:00';
  }

  return '00:00';
}

/**
 * Calculate difference in days between two dates
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Number of days difference
 */
export function daysDifference(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = end.getTime() - start.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
